(() => {
  const WISHLIST_KEY = 'tradezardz:wishlist:v1';
  const RECENT_KEY = 'tradezardz:recent:v1';
  const productCache = new Map();
  let lastWishlistTrigger = null;
  let lastMobileNavTrigger = null;
  const mobileNavigationQuery = window.matchMedia('(max-width: 749px)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const trapFocus = (event, container) => {
    const focusable = Array.from(container?.querySelectorAll(focusableSelector) || []).filter(
      (element) => !element.hidden && element.getClientRects().length
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const readHandles = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle).slice(0, 50) : [];
    } catch (_error) {
      return [];
    }
  };

  const writeHandles = (key, handles) => {
    try {
      localStorage.setItem(key, JSON.stringify(handles));
    } catch (_error) {
      // Private browsing or a full storage quota should not block shopping.
    }
  };

  const shopRoot = () => window.TradeZardzShop?.rootUrl || window.Shopify?.routes?.root || '/';
  const shopCurrency = () => window.TradeZardzShop?.currency || window.Shopify?.currency?.active || 'GBP';

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: shopCurrency(),
      }).format(amount);
    } catch (_error) {
      return `£${amount.toFixed(2)}`;
    }
  };

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${shopRoot()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error('Product unavailable');
      return response.json();
    });
    productCache.set(handle, request);
    return request;
  };

  const productUrl = (product) => product.url || `${shopRoot()}products/${product.handle}`;

  const updateWishlistUi = () => {
    const handles = readHandles(WISHLIST_KEY);
    document.querySelectorAll('[data-tz-wishlist-count]').forEach((element) => {
      element.textContent = handles.length > 99 ? '99+' : String(handles.length);
      element.hidden = handles.length === 0;
    });

    document.querySelectorAll('[data-tz-wishlist-toggle]').forEach((button) => {
      const saved = handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      const label = button.querySelector('[data-tz-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
      button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    });
  };

  const makeProductTile = (product, removable = false) => {
    const article = document.createElement('article');
    article.className = removable ? 'tz-wishlist-item' : 'tz-recent-card';

    const link = document.createElement('a');
    link.className = removable ? 'tz-wishlist-item__image' : 'tz-recent-card__image';
    link.href = productUrl(product);
    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = product.title || '';
      image.loading = 'lazy';
      link.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = removable ? 'tz-wishlist-item__content' : 'tz-recent-card__content';
    const title = document.createElement('a');
    title.className = removable ? 'tz-wishlist-item__title' : 'tz-recent-card__title';
    title.href = productUrl(product);
    title.textContent = product.title;
    const price = document.createElement('div');
    price.className = removable ? 'tz-wishlist-item__price' : 'tz-recent-card__price';
    price.textContent = formatMoney(product.price);
    content.append(title, price);

    if (removable) {
      const actions = document.createElement('div');
      actions.className = 'tz-wishlist-item__actions';
      const view = document.createElement('a');
      view.className = 'button button--secondary';
      view.href = productUrl(product);
      view.textContent = 'View product';
      const remove = document.createElement('button');
      remove.className = 'tz-wishlist-item__remove';
      remove.type = 'button';
      remove.dataset.tzWishlistRemove = product.handle;
      remove.textContent = 'Remove';
      actions.append(view, remove);
      content.appendChild(actions);
    }

    article.append(link, content);
    return article;
  };

  const renderWishlist = async () => {
    const drawer = document.getElementById('TradeZardzWishlistDrawer');
    if (!drawer) return;
    const list = drawer.querySelector('[data-tz-wishlist-list]');
    const empty = drawer.querySelector('[data-tz-wishlist-empty]');
    const handles = readHandles(WISHLIST_KEY);
    list.replaceChildren();
    empty.hidden = handles.length !== 0;
    if (!handles.length) return;

    list.setAttribute('aria-busy', 'true');
    const results = await Promise.allSettled(handles.map(fetchProduct));
    results.forEach((result) => {
      if (result.status === 'fulfilled') list.appendChild(makeProductTile(result.value, true));
    });
    list.removeAttribute('aria-busy');
    empty.hidden = list.children.length !== 0;
  };

  const openWishlist = (trigger) => {
    const drawer = document.getElementById('TradeZardzWishlistDrawer');
    if (!drawer) return;
    lastWishlistTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tz-drawer-open');
    renderWishlist();
    drawer.querySelector('[data-tz-wishlist-close-button]')?.focus();
  };

  const closeWishlist = () => {
    const drawer = document.getElementById('TradeZardzWishlistDrawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tz-drawer-open');
    lastWishlistTrigger?.focus?.();
  };

  const openMobileNavigation = (trigger) => {
    const shell = document.querySelector('[data-tradezardz-global-nav]');
    const navigation = document.getElementById('TradeZardzMobileMenu');
    if (!shell || !navigation || !mobileNavigationQuery.matches) return;
    lastMobileNavTrigger = trigger || document.activeElement;
    shell.classList.add('is-mobile-nav-open');
    navigation.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tz-mobile-nav-open');
    shell.querySelector('[data-tz-mobile-nav-open]')?.setAttribute('aria-expanded', 'true');
    navigation.querySelector('.tz-mobile-nav__close')?.focus();
  };

  const closeMobileNavigation = (restoreFocus = true) => {
    const shell = document.querySelector('[data-tradezardz-global-nav]');
    const navigation = document.getElementById('TradeZardzMobileMenu');
    if (!shell || !navigation) return;
    shell.classList.remove('is-mobile-nav-open');
    document.body.classList.remove('tz-mobile-nav-open');
    shell.querySelector('[data-tz-mobile-nav-open]')?.setAttribute('aria-expanded', 'false');
    if (mobileNavigationQuery.matches) navigation.setAttribute('aria-hidden', 'true');
    else navigation.removeAttribute('aria-hidden');
    closeNavigationMenus();
    if (restoreFocus) lastMobileNavTrigger?.focus?.();
  };

  const initialiseMobileNavigation = () => {
    const navigation = document.getElementById('TradeZardzMobileMenu');
    if (!navigation) return;
    if (mobileNavigationQuery.matches) navigation.setAttribute('aria-hidden', 'true');
    else closeMobileNavigation(false);
  };

  const toggleWishlist = (handle) => {
    if (!handle) return;
    const handles = readHandles(WISHLIST_KEY);
    const next = handles.includes(handle) ? handles.filter((item) => item !== handle) : [handle, ...handles];
    writeHandles(WISHLIST_KEY, next);
    updateWishlistUi();
    if (document.getElementById('TradeZardzWishlistDrawer')?.classList.contains('is-open')) renderWishlist();
  };

  const initialiseRecentlyViewed = async () => {
    const sections = Array.from(document.querySelectorAll('[data-tz-recently-viewed]'));
    if (!sections.length) return;
    const currentHandle = sections.find((section) => section.dataset.currentProduct)?.dataset.currentProduct || '';
    let handles = readHandles(RECENT_KEY);
    if (currentHandle) {
      handles = [currentHandle, ...handles.filter((handle) => handle !== currentHandle)].slice(0, 12);
      writeHandles(RECENT_KEY, handles);
    }

    await Promise.all(
      sections.map(async (section) => {
        const limit = Number(section.dataset.limit || 4);
        const visibleHandles = handles.filter((handle) => handle !== currentHandle).slice(0, limit);
        if (!visibleHandles.length) return;
        const results = await Promise.allSettled(visibleHandles.map(fetchProduct));
        const list = section.querySelector('[data-tz-recently-viewed-list]');
        list.replaceChildren();
        results.forEach((result) => {
          if (result.status === 'fulfilled') list.appendChild(makeProductTile(result.value));
        });
        if (list.children.length) section.hidden = false;
      })
    );
  };

  const closeNavigationMenus = (except = null) => {
    document.querySelectorAll('.tz-site-nav__item.is-open').forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      item.querySelector('[data-tz-nav-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const mobileNavigationOpen = event.target.closest('[data-tz-mobile-nav-open]');
    if (mobileNavigationOpen) {
      event.preventDefault();
      openMobileNavigation(mobileNavigationOpen);
      return;
    }

    if (event.target.closest('[data-tz-mobile-nav-close]')) {
      event.preventDefault();
      closeMobileNavigation();
      return;
    }

    const wishlistToggle = event.target.closest('[data-tz-wishlist-toggle]');
    if (wishlistToggle) {
      event.preventDefault();
      toggleWishlist(wishlistToggle.dataset.productHandle);
      return;
    }

    const wishlistOpen = event.target.closest('[data-tz-wishlist-open]');
    if (wishlistOpen) {
      event.preventDefault();
      openWishlist(wishlistOpen);
      return;
    }

    if (event.target.closest('[data-tz-wishlist-close]')) {
      closeWishlist();
      return;
    }

    const remove = event.target.closest('[data-tz-wishlist-remove]');
    if (remove) {
      toggleWishlist(remove.dataset.tzWishlistRemove);
      return;
    }
    const navTextLink = event.target.closest('.tz-site-nav__item--has-children .tz-site-nav__link');

    if (navTextLink) {
      event.preventDefault();

      const navToggle = navTextLink
        .closest('.tz-site-nav__trigger')
        ?.querySelector('[data-tz-nav-toggle]');

      navToggle?.click();
      return;
    }

    const navToggle = event.target.closest('[data-tz-nav-toggle]');
    if (navToggle) {
      const item = navToggle.closest('.tz-site-nav__item');
      const willOpen = !item.classList.contains('is-open');
      closeNavigationMenus(item);
      item.classList.toggle('is-open', willOpen);
      navToggle.setAttribute('aria-expanded', String(willOpen));
      return;
    }

    if (mobileNavigationQuery.matches && event.target.closest('.tz-site-nav a')) {
      closeMobileNavigation(false);
      return;
    }

    if (!event.target.closest('.tz-site-nav__item')) closeNavigationMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const wishlist = document.getElementById('TradeZardzWishlistDrawer');
      if (wishlist?.classList.contains('is-open')) {
        trapFocus(event, wishlist.querySelector('.tz-wishlist-drawer__panel'));
        return;
      }
      const navigationShell = document.querySelector('[data-tradezardz-global-nav]');
      if (navigationShell?.classList.contains('is-mobile-nav-open')) {
        trapFocus(event, document.getElementById('TradeZardzMobileMenu'));
      }
      return;
    }

    if (event.key !== 'Escape') return;
    if (document.getElementById('TradeZardzWishlistDrawer')?.classList.contains('is-open')) {
      closeWishlist();
    } else if (document.querySelector('[data-tradezardz-global-nav]')?.classList.contains('is-mobile-nav-open')) {
      closeMobileNavigation();
    }
    closeNavigationMenus();
  });

  const initialise = () => {
    updateWishlistUi();
    initialiseRecentlyViewed();
    initialiseMobileNavigation();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();

  document.addEventListener('shopify:section:load', () => {
    updateWishlistUi();
    initialiseRecentlyViewed();
    initialiseMobileNavigation();
  });

  mobileNavigationQuery.addEventListener?.('change', () => closeMobileNavigation(false));
})();

(() => {
  const instances = new Map();
  const init = (root = document) => {
    root.querySelectorAll('[data-tz-carousel]').forEach(section => {
      if (instances.has(section)) return;
      const track = section.querySelector('[data-tz-carousel-track]');
      const controls = section.querySelector('[data-tz-carousel-controls]');
      if (!track || !controls) return;
      const media = matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const on = (node, event, handler, options = {}) => node.addEventListener(event, handler, {...options, signal: controller.signal});
      const pause = controls.querySelector('[data-tz-carousel-pause]');
      let stopped = section.dataset.autoplay !== 'true' || media.matches;
      let hovered = false, touching = false, visible = false, timer;
      const overflow = () => track.scrollWidth > track.clientWidth + 4;
      const sync = () => {
        controls.hidden = !overflow();
        pause.textContent = stopped ? pause.dataset.playLabel : pause.dataset.pauseLabel;
      };
      const move = direction => {
        const cards = [...track.children];
        if (!cards.length) return;
        const step = cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
        const max = track.scrollWidth - track.clientWidth;
        let target = track.scrollLeft + direction * step;
        if (direction > 0 && track.scrollLeft >= max - 4) target = 0;
        if (direction < 0 && track.scrollLeft <= 4) target = max;
        track.scrollTo({left: Math.max(0, Math.min(max, target)), behavior: media.matches ? 'instant' : 'smooth'});
      };
      const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!stopped && !hovered && !touching && visible && !document.hidden && !section.contains(document.activeElement) && !document.querySelector('dialog[open], quick-add-modal[open], .tz-wishlist-drawer.is-open') && overflow()) move(1);
          schedule();
        }, Number(section.dataset.interval) || 5000);
      };
      on(section, 'pointerenter', e => { if (e.pointerType === 'mouse') hovered = true; });
      on(section, 'pointerleave', () => { hovered = false; schedule(); });
      on(track, 'pointerdown', () => { touching = true; });
      on(window, 'pointerup', () => { if (touching) { touching = false; schedule(); } });
      on(window, 'pointercancel', () => { touching = false; schedule(); });
      on(track, 'wheel', schedule, {passive: true});
      on(section, 'focusout', schedule);
      on(pause, 'click', () => { stopped = !stopped; sync(); schedule(); });
      on(controls.querySelector('[data-tz-carousel-prev]'), 'click', () => { move(-1); schedule(); });
      on(controls.querySelector('[data-tz-carousel-next]'), 'click', () => { move(1); schedule(); });
      on(media, 'change', () => { if (media.matches) stopped = true; sync(); });
      const resize = new ResizeObserver(sync);
      resize.observe(track);
      const mutation = new MutationObserver(sync);
      mutation.observe(track, {childList: true});
      const intersection = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, {threshold: .15});
      intersection.observe(section);
      instances.set(section, () => { clearTimeout(timer); controller.abort(); resize.disconnect(); mutation.disconnect(); intersection.disconnect(); });
      sync(); schedule();
    });
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, section) => { if (event.target.contains(section)) { destroy(); instances.delete(section); } });
  });
})();
